import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
  parseFractionLikeInput,
} from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

const TYPE = "MaturaTrapezoidSimilarityLengthOpenTask";

// Każdy zestaw zachowuje konstrukcję oryginału:
// ABE ~ DCE, AE / AB = DE / CD oraz trójkąt ABE jest prostokątny.
export const VARIANTS = [
  { ab: 6, cd: 3, ae: 8, de: 4, be: 10 },
  { ab: 9, cd: 6, ae: 12, de: 8, be: 15 },
  { ab: 10, cd: 5, ae: 24, de: 12, be: 26 },
  { ab: 15, cd: 9, ae: 20, de: 12, be: 25 },
  { ab: 16, cd: 8, ae: 30, de: 15, be: 34 },
  { ab: 18, cd: 12, ae: 24, de: 16, be: 30 },
];

export const ORIGINAL = { ab: 12, cd: 6, ae: 16, de: 8, be: 20 };

export function makeTask(values, source, angleVariant = "E") {
  return {
    type: TYPE,
    data: {
      ...values,
      ad: values.ae + values.de,
      maxPoints: 2,
      angleVariant,
    },
    ...(source ? { source } : {}),
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2024_SIE") {
    return makeTask(ORIGINAL, {
      variant: "original",
      exam: "MATURA",
      session: "2024_SIE",
      taskNumber: "20",
    }, "E");
  }

  const values = VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  return makeTask(values, undefined, Math.random() < 0.5 ? "E" : "BC");
}

function angleConditionLatex(angleVariant) {
  return angleVariant === "BC"
    ? String.raw`\angle ABE=\angle DCE`
    : String.raw`\angle BEA=\angle CED`;
}

function TrapezoidDiagram({ angleVariant = "E", explanation = false }) {
  return (
    <svg
      viewBox="0 0 420 480"
      role="img"
      aria-label="Trapez prostokątny ABCD z punktem E na wysokości AD oraz odcinkami BE i CE"
      style={{
        display: "block",
        width: explanation ? "min(100%, 310px)" : "min(100%, 340px)",
        height: "auto",
        margin: explanation ? "12px auto 18px" : "14px auto",
        color: "var(--color-text, currentColor)",
      }}
    >
      <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M88 422 L342 422 L255 58 L88 58 Z" />
        <path d="M88 172 L342 422" />
        <path d="M88 172 L255 58" />
      </g>

      <g fill="none" stroke="var(--color-accent, #ff9800)" strokeWidth="3">
        <path d="M88 402 A20 20 0 0 1 108 422" />
        <path d="M108 58 A20 20 0 0 1 88 78" />
        {angleVariant === "E" ? (
          <>
            <path d="M88 197 A25 25 0 0 0 106 189" />
            <path d="M88 147 A25 25 0 0 1 106 154" />
          </>
        ) : (
          <>
            <path d="M317 422 A25 25 0 0 1 324 405" />
            <path d="M230 58 A25 25 0 0 0 234 72" />
          </>
        )}
      </g>

      <g fill="var(--color-accent, #ff9800)">
        <circle cx="96" cy="414" r="3.5" />
        <circle cx="96" cy="66" r="3.5" />
      </g>

      <g fill="currentColor" fontFamily="KaTeX_Main, serif" fontStyle="italic" fontSize="25">
        <text x="59" y="447">A</text>
        <text x="350" y="447">B</text>
        <text x="263" y="52">C</text>
        <text x="58" y="52">D</text>
        <text x="58" y="181">E</text>
      </g>
    </svg>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne – otwarte (0–2 pkt).</strong></p>
      <p>
        Podstawy trapezu prostokątnego <InlineMath math="ABCD" /> mają długości:{" "}
        <InlineMath math={`|AB|=${data.ab}`} /> oraz <InlineMath math={`|CD|=${data.cd}`} />.
        Wysokość <InlineMath math="AD" /> tego trapezu ma długość{" "}
        <InlineMath math={String(data.ad)} />. Na odcinku <InlineMath math="AD" /> leży punkt{" "}
        <InlineMath math="E" /> taki, że <InlineMath math={angleConditionLatex(data.angleVariant)} />.
      </p>
      <TrapezoidDiagram angleVariant={data.angleVariant} />
      <p>
        <strong>
          Oblicz długość odcinka <InlineMath math="BE" />. Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

function answerValue(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") return parseFractionLikeInput(value);
  if (value && typeof value === "object") {
    if (typeof value.raw === "string") return parseFractionLikeInput(value.raw);
    if (typeof value.value === "string") return parseFractionLikeInput(value.value);
    if (typeof value.parsed === "number") return value.parsed;
  }
  return null;
}

function isExpected(value, expected) {
  const parsed = answerValue(value);
  return Number.isFinite(parsed) && Math.abs(parsed - expected) < 1e-8;
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  const update = (key, value) => onChange({ ...answer, [key]: value });

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 520 }}>
      <OpenTaskScoringHint variant="proof">
        Poprawnie wyznaczone <InlineMath math="AE" /> lub <InlineMath math="DE" /> daje punkt
        pośredni. Pełne 2 punkty wymagają poprawnej metody i długości <InlineMath math="BE" />.
      </OpenTaskScoringHint>
      {[
        ["ae", "|AE|"],
        ["de", "|DE|"],
        ["be", "|BE|"],
      ].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <InlineMath math={`${label}=`} />
          <FractionInput
            value={answer[key] || ""}
            onChange={(value) => update(key, value)}
            disabled={disabled}
            label={label}
            showPreview={false}
            placeholder="Wpisz wynik"
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const answer = userAnswer || {};
  const aeOk = isExpected(answer.ae, data.ae);
  const deOk = isExpected(answer.de, data.de);
  const beOk = isExpected(answer.be, data.be);
  const intermediateOk = aeOk || deOk;
  const score = intermediateOk && beOk ? 2 : intermediateOk ? 1 : 0;

  return {
    isCorrect: score === 2,
    correct: score === 2,
    score,
    maxScore: 2,
    parts: { ae: aeOk, de: deOk, intermediate: intermediateOk, be: beOk },
    expected: { ae: data.ae, de: data.de, be: data.be },
  };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ display: "grid", gap: 5 }}>
      <InlineMath math={`|AE|=${data.ae}`} />
      <InlineMath math={`|DE|=${data.de}`} />
      <InlineMath math={`|BE|=${data.be}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const answer = userAnswer || {};
  const line = (key, label) => {
    const raw = typeof answer[key] === "string" ? answer[key] : answer[key]?.raw || "";
    return raw ? <InlineMath math={formatLatexPreview(raw, label)} /> : <em>brak odpowiedzi</em>;
  };

  return (
    <div style={{ display: "grid", gap: 5 }}>
      {line("ae", "|AE|")}
      {line("de", "|DE|")}
      {line("be", "|BE|")}
    </div>
  );
}

export function renderExplanation({ data }) {
  const usesAnglesAtE = data.angleVariant !== "BC";

  return (
    <div style={{ textAlign: "left" }}>
      <TrapezoidDiagram angleVariant={data.angleVariant} explanation />
      <p>
        <strong>Krok 1.</strong> Trójkąty <InlineMath math="ABE" /> i{" "}
        <InlineMath math="DCE" /> są podobne na podstawie cechy kąt–kąt–kąt:
        kąty przy <InlineMath math="A" /> i <InlineMath math="D" /> są proste, a{" "}
        {usesAnglesAtE ? "z treści zadania" : "drugą parę kątów odpowiadających sobie tworzą"}{" "}
        <InlineMath math={angleConditionLatex(data.angleVariant)} />.
        {!usesAnglesAtE && (
          <> Trzecia para kątów jest więc również równa.</>
        )}
        {" "}Odpowiadające sobie wierzchołki to kolejno{" "}
        <InlineMath math={String.raw`A\leftrightarrow D`} />,{" "}
        <InlineMath math={String.raw`B\leftrightarrow C`} /> oraz{" "}
        <InlineMath math={String.raw`E\leftrightarrow E`} />.
      </p>
      <BlockMath math={`\\frac{|AE|}{${data.ab}}=\\frac{|DE|}{${data.cd}}`} />
      <p>
        Ponieważ <InlineMath math={`|DE|=${data.ad}-|AE|`} />, otrzymujemy:
      </p>
      <BlockMath
        math={`\\frac{|AE|}{${data.ab}}=\\frac{${data.ad}-|AE|}{${data.cd}}`}
      />
      <BlockMath
        math={`${data.cd}|AE|=${data.ab}(${data.ad}-|AE|)`}
      />
      <BlockMath
        math={`${data.ab + data.cd}|AE|=${data.ab * data.ad}\\quad\\Longrightarrow\\quad |AE|=${data.ae}`}
      />
      <BlockMath math={`|DE|=${data.ad}-${data.ae}=${data.de}`} />

      <p>
        <strong>Krok 2.</strong> Trójkąt <InlineMath math="ABE" /> jest prostokątny.
        Z twierdzenia Pitagorasa:
      </p>
      <BlockMath
        math={`|BE|^2=|AB|^2+|AE|^2=${data.ab}^2+${data.ae}^2=${data.be ** 2}`}
      />
      <BlockMath math={`|BE|=${data.be}`} />
      <p>
        Zatem długość odcinka <InlineMath math="BE" /> jest równa{" "}
        <InlineMath math={String(data.be)} />.
      </p>
    </div>
  );
}
